/**
 * Your own endpoint, holding your own key.
 *
 * This is the production answer. The browser posts the conversation to an
 * address you control; that address talks to whichever model you like and
 * returns the same small shape. No key is ever in a browser, the model can be
 * changed without shipping an app update, and you can log, rate-limit or refuse
 * a request before it costs anything.
 *
 * The contract is deliberately tiny, because writing the other half of it should
 * take an afternoon:
 *
 *   POST <baseUrl>
 *   → { "system": string,
 *       "turns":  [{ role, text?, calls?, results? }],
 *       "tools":  [{ name, description, schema }] }
 *
 *   ← { "text": string, "calls": [{ id, name, input }] }
 *
 * `turns` and `tools` are exactly the types in `./types.ts`, so a proxy that
 * forwards to Anthropic or OpenAI is mostly the corresponding adapter in this
 * folder moved to a server. A reply with no `calls` is fine; so is one with no
 * `text`. Anything else in the body is ignored.
 */
import type { AskReply, AskRequest, AssistantProvider, ToolCall } from './types';
import { httpError, networkError } from './types';

export function createProxyProvider(opts: { baseUrl: string; apiKey?: string }): AssistantProvider {
  const url = opts.baseUrl.trim();
  return {
    id: 'proxy',
    label: 'Your own endpoint',
    ready: () => !!url,
    notReadyKey: () => (url ? null : 'assistant.error.noUrl'),
    async ask({ system, turns, tools, signal }: AskRequest): Promise<AskReply> {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      // Optional: a shared secret so the endpoint is not open to the world. Not
      // a model key — that stays on the server.
      if (opts.apiKey?.trim()) headers.authorization = `Bearer ${opts.apiKey.trim()}`;
      let res: Response;
      try {
        res = await fetch(url, { method: 'POST', signal, headers, body: JSON.stringify({ system, turns, tools }) });
      } catch (e) {
        throw networkError(e);
      }
      if (!res.ok) throw await httpError(res);

      const data = (await res.json()) as { text?: string; calls?: ToolCall[] };
      return {
        text: (data.text ?? '').trim(),
        // Trust the shape only as far as it is checkable: a malformed call would
        // otherwise reach the tool runner as `undefined.name`.
        calls: (data.calls ?? []).filter((c) => c && typeof c.name === 'string').map((c) => ({ id: c.id ?? c.name, name: c.name, input: c.input ?? {} })),
      };
    },
  };
}
